<script lang="ts" setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid, usePagination } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { AdminCartsResponse, AdminCartStatus, type AdminCart, type AdminCartItem } from '@/types/fulfillment'
import { DashboardReasonConfirmDialog } from '@/components/dashboard/common'

defineOptions({ name: 'OrdersList' })

const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const carts = ref<AdminCart[]>([])
const pagination = usePagination({ initialItemsPerPage: 25 })
const expandedCarts = ref<Set<string>>(new Set())

// Filters
const statusFilter = ref<string>('all')
const accountIdFilter = ref('')
const cartIdFilter = ref('')

const statusOptions = computed(() => [
  { value: 'all', title: t('dashboard.admin.fulfillment.orders.filters.allStatuses') },
  ...AdminCartStatus.options.map((status) => ({
    value: status,
    title: t(`dashboard.admin.fulfillment.orders.status.${status}`)
  }))
])

const hasActiveFilters = computed(
  () => statusFilter.value !== 'all' || accountIdFilter.value.trim() !== '' || cartIdFilter.value.trim() !== ''
)

// Refund is only possible once the payment has actually been captured.
// The backend rejects carts without a payment or already refunded ones;
// uncaptured statuses (started/open/payment_*/pending_fulfillment) cannot be refunded via Stripe.
const REFUNDABLE_STATUSES: ReadonlySet<string> = new Set(['done', 'fulfillment', 'partial_fulfillment'])

function isRefundable(cart: AdminCart): boolean {
  return REFUNDABLE_STATUSES.has(cart.status) && !!cart.payment_id
}

const refundDialog = ref({
  show: false,
  cart: null as AdminCart | null,
  loading: false
})

async function fetchCarts() {
  loading.value = true

  try {
    const { limit, offset } = pagination.buildQueryParams()
    let url = `/admin/carts?limit=${limit}&offset=${offset}`

    if (statusFilter.value !== 'all') {
      url += `&status=${statusFilter.value}`
    }
    if (accountIdFilter.value.trim()) {
      url += `&account_id=${encodeURIComponent(accountIdFilter.value.trim())}`
    }
    if (cartIdFilter.value.trim()) {
      url += `&cart_id=${encodeURIComponent(cartIdFilter.value.trim())}`
    }

    const res = await useAxios<null, AdminCartsResponse>('GET', url, true)

    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminCartsResponse.safeParse(res.data)
      if (!validate.success) {
        console.error('Admin carts validation failed:', validate.error)
        return
      }
      carts.value = validate.data.carts
      pagination.setTotal(validate.data.count)
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'OrdersList' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(err as Error)
    })
  } finally {
    loading.value = false
  }
}

watch(() => pagination.page.value, () => {
  fetchCarts()
})

watch([statusFilter], () => {
  pagination.goToPage(1)
  fetchCarts()
})

function applyFilters() {
  pagination.goToPage(1)
  fetchCarts()
}

function clearFilters() {
  statusFilter.value = 'all'
  accountIdFilter.value = ''
  cartIdFilter.value = ''
  pagination.goToPage(1)
  fetchCarts()
}

function toggleExpand(cartId: string) {
  if (expandedCarts.value.has(cartId)) {
    expandedCarts.value.delete(cartId)
  } else {
    expandedCarts.value.add(cartId)
  }
}

function isExpanded(cartId: string): boolean {
  return expandedCarts.value.has(cartId)
}

function formatDate(dateString?: string | null): string {
  if (!dateString) return '—'
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'done': return 'status-done'
    case 'refunded': return 'status-refunded'
    case 'fulfillment':
    case 'pending_fulfillment':
    case 'partial_fulfillment': return 'status-fulfillment'
    case 'payment_processing': return 'status-processing'
    case 'payment_failed':
    case 'payment_canceled': return 'status-failed'
    default: return 'status-neutral'
  }
}

function itemLabel(item: AdminCartItem): string {
  return item.product_name || item.product_slug || `#${item.product_id}`
}

function truncateId(id: string): string {
  if (id.length <= 12) return id
  return `${id.substring(0, 6)}...${id.substring(id.length - 4)}`
}

// Pagination display helpers
const showingFrom = computed(() => {
  if (!carts.value?.length) return 0
  return ((pagination.page.value - 1) * pagination.itemsPerPage.value) + 1
})

const showingTo = computed(() => {
  return Math.min(showingFrom.value + (carts.value?.length ?? 0) - 1, pagination.total.value)
})

function openRefundDialog(cart: AdminCart) {
  refundDialog.value.cart = cart
  refundDialog.value.show = true
}

async function confirmRefund(reason: string) {
  const cart = refundDialog.value.cart
  if (!cart) return

  refundDialog.value.loading = true

  try {
    const res = await useAxios<{ reason: string }, { message?: string }>(
      'POST',
      `/admin/cart/${cart.id}/refund`,
      true,
      { reason }
    )

    if (res && res.status === STATUS_CODES.OK) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t('dashboard.admin.fulfillment.refund.success'),
        message: cart.account.email
      })
      refundDialog.value.show = false
      fetchCarts()
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'OrdersList:refund' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: getErrorMessage(err as Error)
    })
  } finally {
    refundDialog.value.loading = false
  }
}

onMounted(() => {
  fetchCarts()
})
</script>

<template>
  <div class="orders-list">
    <!-- Header -->
    <div class="list-header">
      <div class="header-left">
        <div class="header-icon">
          <v-icon icon="mdi-package-variant-closed" size="20" />
        </div>
        <div>
          <h2 class="list-title">{{ t('dashboard.admin.fulfillment.orders.title') }}</h2>
          <p class="list-subtitle">
            {{ t('dashboard.admin.fulfillment.orders.subtitle', { count: pagination.total.value }) }}
          </p>
        </div>
      </div>
      <button class="refresh-btn" @click="fetchCarts">
        <v-icon icon="mdi-refresh" size="18" />
      </button>
    </div>

    <!-- Filters -->
    <div class="filters-container">
      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        item-title="title"
        item-value="value"
        :label="t('dashboard.admin.fulfillment.orders.filters.status')"
        variant="outlined"
        density="compact"
        hide-details
        class="filter-select glass-input"
      />
      <v-text-field
        v-model="accountIdFilter"
        :label="t('dashboard.admin.fulfillment.orders.filters.accountId')"
        :placeholder="t('dashboard.admin.fulfillment.orders.filters.accountIdPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        class="filter-input glass-input"
        @keyup.enter="applyFilters"
      />
      <v-text-field
        v-model="cartIdFilter"
        :label="t('dashboard.admin.fulfillment.orders.filters.cartId')"
        :placeholder="t('dashboard.admin.fulfillment.orders.filters.cartIdPlaceholder')"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        class="filter-input glass-input"
        @keyup.enter="applyFilters"
      >
        <template #append-inner>
          <v-icon icon="mdi-magnify" size="18" class="search-icon" @click="applyFilters" />
        </template>
      </v-text-field>
      <button
        v-if="hasActiveFilters"
        class="clear-filters-btn"
        @click="clearFilters"
      >
        <v-icon icon="mdi-close" size="16" />
        {{ t('dashboard.admin.fulfillment.orders.filters.clear') }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular color="orange" indeterminate size="40" width="3" />
      <span class="loading-text">{{ t('dashboard.admin.fulfillment.orders.loading') }}</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="carts.length === 0" class="empty-state">
      <div class="empty-icon">
        <v-icon icon="mdi-package-variant" size="48" />
      </div>
      <span class="empty-title">{{ t('dashboard.admin.fulfillment.orders.empty.title') }}</span>
      <span class="empty-subtitle">
        {{ hasActiveFilters
          ? t('dashboard.admin.fulfillment.orders.empty.filtered')
          : t('dashboard.admin.fulfillment.orders.empty.none') }}
      </span>
    </div>

    <!-- Orders Table -->
    <template v-else>
      <div class="orders-table">
        <div class="table-head">
          <div class="head-cell cell-cart">{{ t('dashboard.admin.fulfillment.orders.table.cartId') }}</div>
          <div class="head-cell cell-account">{{ t('dashboard.admin.fulfillment.orders.table.customer') }}</div>
          <div class="head-cell cell-status">{{ t('dashboard.admin.fulfillment.orders.table.status') }}</div>
          <div class="head-cell cell-updated">{{ t('dashboard.admin.fulfillment.orders.table.updated') }}</div>
          <div class="head-cell cell-items">{{ t('dashboard.admin.fulfillment.orders.table.items') }}</div>
          <div class="head-cell cell-expand"></div>
        </div>

        <div
          v-for="cart in carts"
          :key="cart.id"
          class="cart-row-wrapper"
        >
          <!-- Main Row -->
          <div class="table-row" :class="{ expanded: isExpanded(cart.id) }">
            <div class="row-cell cell-cart">
              <span class="cart-id">{{ truncateId(cart.id) }}</span>
            </div>
            <div class="row-cell cell-account">
              <span class="account-email">{{ cart.account.email }}</span>
              <span class="account-id">#{{ cart.account.id }}</span>
            </div>
            <div class="row-cell cell-status">
              <span class="status-badge" :class="getStatusClass(cart.status)">
                {{ t(`dashboard.admin.fulfillment.orders.status.${cart.status}`) }}
              </span>
            </div>
            <div class="row-cell cell-updated">
              <span class="date-text">{{ formatDate(cart.updated) }}</span>
            </div>
            <div class="row-cell cell-items">
              <span class="items-count">
                {{ t('dashboard.admin.fulfillment.orders.table.itemCount', cart.items.length) }}
              </span>
            </div>
            <div class="row-cell cell-expand">
              <button class="expand-btn" @click="toggleExpand(cart.id)">
                <v-icon
                  :icon="isExpanded(cart.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  size="20"
                />
              </button>
            </div>
          </div>

          <!-- Expanded Details -->
          <div v-if="isExpanded(cart.id)" class="expanded-content">
            <!-- Items -->
            <div class="detail-section">
              <h4 class="section-title">
                <v-icon icon="mdi-package-variant" size="16" />
                {{ t('dashboard.admin.fulfillment.orders.details.items') }}
              </h4>
              <div v-if="cart.items.length" class="detail-table">
                <div
                  v-for="(item, idx) in cart.items"
                  :key="idx"
                  class="detail-row"
                >
                  <span class="item-name">{{ item.count }}x {{ itemLabel(item) }}</span>
                  <span v-if="item.domain" class="item-domain">{{ item.domain }}</span>
                  <span v-if="item.billing_freq" class="item-freq">
                    {{ t(`dashboard.admin.fulfillment.orders.billingFreq.${item.billing_freq}`) }}
                  </span>
                </div>
              </div>
              <p v-else class="detail-empty">{{ t('dashboard.admin.fulfillment.orders.details.noItems') }}</p>
            </div>

            <!-- Payment / Refund Info -->
            <div class="detail-section">
              <h4 class="section-title">
                <v-icon icon="mdi-credit-card-outline" size="16" />
                {{ t('dashboard.admin.fulfillment.orders.details.payment') }}
              </h4>
              <div class="payment-grid">
                <div class="payment-field">
                  <span class="field-label">{{ t('dashboard.admin.fulfillment.orders.details.paymentId') }}</span>
                  <span class="field-value monospace">{{ cart.payment_id || '—' }}</span>
                </div>
                <div class="payment-field">
                  <span class="field-label">{{ t('dashboard.admin.fulfillment.orders.details.created') }}</span>
                  <span class="field-value">{{ formatDate(cart.created) }}</span>
                </div>
                <div class="payment-field">
                  <span class="field-label">{{ t('dashboard.admin.fulfillment.orders.details.paidAt') }}</span>
                  <span class="field-value">{{ formatDate(cart.date_paid) }}</span>
                </div>
                <div v-if="cart.refunded_at" class="payment-field">
                  <span class="field-label">{{ t('dashboard.admin.fulfillment.orders.details.refundedAt') }}</span>
                  <span class="field-value">{{ formatDate(cart.refunded_at) }}</span>
                </div>
                <div v-if="cart.refund_reason" class="payment-field field-wide">
                  <span class="field-label">{{ t('dashboard.admin.fulfillment.orders.details.refundReason') }}</span>
                  <span class="field-value">{{ cart.refund_reason }}</span>
                </div>
              </div>
            </div>

            <!-- Cart Actions -->
            <div class="cart-actions">
              <span v-if="cart.status === 'refunded'" class="refunded-badge">
                <v-icon icon="mdi-credit-card-check" size="14" />
                {{ t('dashboard.admin.fulfillment.refund.refunded') }}
              </span>
              <button
                v-else-if="isRefundable(cart)"
                class="refund-btn"
                @click="openRefundDialog(cart)"
              >
                <v-icon icon="mdi-credit-card-refund" size="16" />
                <span>{{ t('dashboard.admin.fulfillment.refund.button') }}</span>
              </button>
              <span v-else class="not-refundable-hint">
                {{ t('dashboard.admin.fulfillment.orders.details.notRefundable') }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="pagination-container">
        <span class="pagination-info">
          {{ t('dashboard.admin.fulfillment.orders.pagination.showing', {
            from: showingFrom,
            to: showingTo,
            total: pagination.total.value
          }) }}
        </span>
        <div class="pagination-controls">
          <button
            class="page-btn"
            :disabled="!pagination.hasPrevPage.value"
            @click="pagination.prevPage()"
          >
            <v-icon icon="mdi-chevron-left" size="18" />
          </button>
          <span class="page-indicator">
            {{ t('dashboard.admin.fulfillment.orders.pagination.page', {
              page: pagination.page.value,
              pages: pagination.totalPages.value
            }) }}
          </span>
          <button
            class="page-btn"
            :disabled="!pagination.hasNextPage.value"
            @click="pagination.nextPage()"
          >
            <v-icon icon="mdi-chevron-right" size="18" />
          </button>
        </div>
      </div>
    </template>

    <!-- Refund Confirmation Dialog -->
    <DashboardReasonConfirmDialog
      v-model="refundDialog.show"
      :title="t('dashboard.admin.fulfillment.refund.dialogTitle')"
      :subtitle="refundDialog.cart?.account.email"
      :description="t('dashboard.admin.fulfillment.refund.dialogMessage')"
      :warning="t('dashboard.admin.fulfillment.refund.warning')"
      icon="mdi-credit-card-refund"
      variant="danger"
      :confirm-text="t('dashboard.admin.fulfillment.refund.confirm')"
      confirm-icon="mdi-credit-card-refund"
      :loading="refundDialog.loading"
      :reason-label="t('dashboard.admin.fulfillment.refund.reasonLabel')"
      :reason-placeholder="t('dashboard.admin.fulfillment.refund.reasonPlaceholder')"
      @confirm="confirmRefund"
      @cancel="refundDialog.cart = null"
    />
  </div>
</template>

<style lang="scss" scoped>
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 10px;

  .v-icon {
    color: #00b0ff;
  }
}

.list-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.list-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Filters */
.filters-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 14px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.filter-select {
  max-width: 220px;
  min-width: 180px;
}

.filter-input {
  max-width: 280px;
  min-width: 200px;
}

.search-icon {
  cursor: pointer;
  color: rgba(200, 218, 255, 0.5);

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }
}

.clear-filters-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.6);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Loading & Empty States */
.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 200px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 16px;

  .v-icon {
    color: rgba(200, 218, 255, 0.25);
  }
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.empty-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Table */
.orders-table {
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.table-head {
  display: grid;
  grid-template-columns: 120px 1fr 170px 140px 110px 50px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.head-cell {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cart-row-wrapper {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.table-row {
  display: grid;
  grid-template-columns: 120px 1fr 170px 140px 110px 50px;
  padding: 14px 16px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &.expanded {
    background: rgba(0, 176, 255, 0.04);
  }
}

.row-cell {
  display: flex;
  align-items: center;
}

.cart-id {
  font-family: monospace;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.7);
}

.cell-account {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.account-email {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
}

.account-id {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.date-text {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.items-count {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
}

.expand-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
  }
}

/* Status Badges */
.status-badge {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;

  &.status-done {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }

  &.status-refunded {
    background: rgba(156, 39, 176, 0.15);
    color: #ce93d8;
  }

  &.status-fulfillment {
    background: rgba(0, 176, 255, 0.15);
    color: #4fc3f7;
  }

  &.status-processing {
    background: rgba(255, 193, 7, 0.15);
    color: #ffc107;
  }

  &.status-failed {
    background: rgba(239, 83, 80, 0.15);
    color: #ef5350;
  }

  &.status-neutral {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.7);
  }
}

/* Expanded Content */
.expanded-content {
  padding: 16px;
  background: rgba(15, 23, 48, 0.3);
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.detail-section {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.8);

  .v-icon {
    color: rgba(200, 218, 255, 0.5);
  }
}

.detail-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 14px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 8px;
}

.item-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.item-domain {
  font-family: monospace;
  font-size: 0.8rem;
  color: #4fc3f7;
}

.item-freq {
  padding: 2px 8px;
  background: rgba(120, 163, 255, 0.1);
  border-radius: 4px;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.7);
}

.detail-empty {
  margin: 0;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Payment Grid */
.payment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.payment-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 8px;

  &.field-wide {
    grid-column: 1 / -1;
  }
}

.field-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.field-value {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  word-break: break-all;

  &.monospace {
    font-family: monospace;
    font-size: 0.8rem;
  }
}

/* Cart Actions */
.cart-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.refund-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(239, 83, 80, 0.12);
  border: 1px solid rgba(239, 83, 80, 0.3);
  border-radius: 8px;
  color: #ef5350;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 83, 80, 0.22);
    border-color: rgba(239, 83, 80, 0.45);
  }
}

.refunded-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(156, 39, 176, 0.12);
  border: 1px solid rgba(156, 39, 176, 0.25);
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #ce93d8;
}

.not-refundable-hint {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.4);
}

/* Pagination */
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.pagination-info {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 6px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
    color: rgba(255, 255, 255, 0.9);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
}

/* Light Theme */
.v-theme--lightTheme {
  .list-title,
  .empty-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .list-subtitle,
  .empty-subtitle,
  .loading-text {
    color: rgba(15, 23, 42, 0.6);
  }

  .orders-table,
  .filters-container,
  .loading-container,
  .empty-state {
    background: rgba(255, 255, 255, 0.6);
  }

  .account-email,
  .item-name,
  .field-value {
    color: rgba(15, 23, 42, 0.9);
  }

  .cart-id,
  .account-id,
  .date-text,
  .items-count,
  .pagination-info,
  .page-indicator {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Responsive */
@media (max-width: 900px) {
  .table-head,
  .table-row {
    grid-template-columns: 1fr 130px 50px;
  }

  .cell-cart,
  .cell-updated,
  .cell-items {
    display: none;
  }
}

@media (max-width: 600px) {
  .table-head {
    display: none;
  }

  .table-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px;
  }

  .row-cell {
    width: 100%;
    justify-content: space-between;
  }

  .cell-cart,
  .cell-updated,
  .cell-items {
    display: flex;
  }

  .cell-expand {
    justify-content: flex-end;
  }
}
</style>
